'use client';

// Full-page status grid. Same /_status JSON the landing badge polls,
// rendered as a per-system × per-network table. Uses the same
// poll-and-tab-back-refresh pattern as StatusBadge so a single
// network event updates both surfaces in lock-step.

import { useEffect, useState } from 'react';

interface StatusJson {
  ok:     boolean;
  as_of:  string;
  systems: Record<string, Record<string, 'ok' | 'degraded'>>;
}

const POLL_INTERVAL_MS = 30_000;
const NETWORK_ORDER = ['mainnet', 'chipnet', 'testnet4', 'regtest'] as const;
const SYSTEM_ORDER  = ['bchn', 'fulcrum', 'chaingraph'] as const;

export function StatusGrid() {
  const [data, setData] = useState<StatusJson | null>(null);
  const [err,  setErr]  = useState<string | null>(null);
  const [last, setLast] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function refresh() {
      try {
        const res = await fetch('/_status', { cache: 'no-store' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const j = await res.json() as StatusJson;
        if (cancelled) return;
        setData(j); setErr(null); setLast(new Date());
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    }
    void refresh();
    timer = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (err && !data) {
    return (
      <div className="card text-sm text-red-300">
        Status endpoint unreachable: {err}. Try again in a moment.
      </div>
    );
  }
  if (!data) {
    return <div className="card text-sm text-ink-dim">Loading status…</div>;
  }

  // Union of (system, network) we know about: gateway-reported plus
  // the canonical network catalog. Reported ones get their actual
  // state; unknown ones get "no data" — better than hiding them.
  const present_systems: string[] = Array.from(new Set([
    ...SYSTEM_ORDER,
    ...Object.keys(data.systems),
  ]));

  return (
    <div className="space-y-6">
      <div className={`card ring-1 ${data.ok ? 'ring-emerald-500/30' : 'ring-amber-500/30'}`}>
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${data.ok ? 'bg-emerald-400' : 'bg-amber-400'}`} />
          <span className="text-lg font-semibold">
            {data.ok ? 'All systems operational' : 'Some endpoints degraded'}
          </span>
        </div>
        <p className="mt-2 text-xs text-ink-faint">
          As of {new Date(data.as_of).toLocaleString()}
          {last && ` · Last refreshed ${last.toLocaleTimeString()}`}
        </p>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-faint">
            <tr className="border-b border-white/5">
              <th className="font-normal px-4 py-3">System</th>
              {NETWORK_ORDER.map((n) => (
                <th key={n} className="font-normal px-4 py-3">{n}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {present_systems.map((sys) => (
              <tr key={sys} className="border-t border-white/5">
                <td className="px-4 py-3 font-mono text-xs text-ink capitalize">{sys}</td>
                {NETWORK_ORDER.map((n) => (
                  <td key={n} className="px-4 py-3">
                    <Cell state={data.systems[sys]?.[n]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cell({ state }: { state: 'ok' | 'degraded' | undefined }) {
  if (state === 'ok') {
    return (
      <span className="inline-flex items-center gap-2 text-emerald-300 text-xs">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> ok
      </span>
    );
  }
  if (state === 'degraded') {
    return (
      <span className="inline-flex items-center gap-2 text-amber-300 text-xs">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> degraded
      </span>
    );
  }
  return <span className="text-xs text-ink-faint">—</span>;
}
